import { useEffect, useState } from 'react';
import Terminal from './Terminal';
import ChatWidget from './ChatWidget';
import LiquidEther from './bits/LiquidEther';
import DotGrid from './bits/DotGrid';
import ClickSpark from './bits/ClickSpark';
import DecryptedText from './bits/DecryptedText';
import StarBorder from './bits/StarBorder';
import GlareHover from './bits/GlareHover';
import './Home.css';

// ── DATA ─────────────────────────────────────────────────────────────────────

const SERVICES = [
  {
    icon: '🤖',
    name: 'AI Agents & Automation',
    desc: 'LLM-powered agents that think, reason, and act. We build autonomous systems that integrate with your workflows and amplify your team\'s output by 10x.',
    tags: ['LangChain', 'OpenAI', 'AutoGen', 'n8n'],
  },
  {
    icon: '⛓️',
    name: 'Web3 & Blockchain',
    desc: 'DeFi protocols, NFT platforms, and multi-chain solutions. We build decentralized systems that are battle-tested, secure, and production-ready at scale.',
    tags: ['Solidity', 'Ethers.js', 'Hardhat', 'IPFS'],
  },
  {
    icon: '☁️',
    name: 'DevOps & Cloud',
    desc: 'Bulletproof infrastructure on AWS, GCP & Azure. CI/CD pipelines, Kubernetes orchestration, and monitoring that keeps your systems always-on.',
    tags: ['AWS', 'Docker', 'Kubernetes', 'Terraform'],
  },
  {
    icon: '🏗️',
    name: 'SaaS Development',
    desc: 'Full-stack SaaS products from zero to enterprise. Multi-tenant architectures, subscription billing, and dashboards designed for retention.',
    tags: ['Next.js', 'Node.js', 'PostgreSQL', 'Stripe'],
  },
  {
    icon: '🏘️',
    name: 'PropTech & RETS',
    desc: 'Real estate platforms, MLS integrations, and property management systems. We turn complex RETS feeds into fast, searchable, beautiful applications.',
    tags: ['RETS', 'MLS API', 'IDX', 'Mapbox'],
  },
  {
    icon: '🔐',
    name: 'Self-Sovereign Identity',
    desc: 'Decentralized identity via SSI, DIDs & verifiable credentials. We build the trust layer for the next generation of digital interactions.',
    tags: ['DID', 'VC', 'Hyperledger', 'W3C'],
  },
];

const REVIEWS = [
  {
    initials: 'JM', grad: 'linear-gradient(135deg,#E5283A,#FF4D5E)',
    name: 'James M.', role: 'CTO, TechStart Inc.',
    text: 'Azaz delivered a production-ready AI agent system in 3 weeks that would have taken our in-house team months. The architecture was clean, documentation thorough, and the results exceeded every benchmark we set.',
  },
  {
    initials: 'SR', grad: 'linear-gradient(135deg,#F59E0B,#EF4444)',
    name: 'Sarah R.', role: 'Founder, BlockFin Labs',
    text: 'Best developer we\'ve ever worked with — bar none. Our DeFi protocol launched on time, under budget, and has processed over $2M in transactions without a single exploit. Truly exceptional work.',
  },
  {
    initials: 'DK', grad: 'linear-gradient(135deg,#10B981,#0EA5E9)',
    name: 'David K.', role: 'VP Engineering, PropSearch',
    text: 'Rebuilt our entire RETS integration from scratch. What was a brittle mess is now a rock-solid pipeline ingesting 50K listings daily. Communication was perfect and delivery was ahead of schedule.',
  },
  {
    initials: 'MH', grad: 'linear-gradient(135deg,#7A1420,#E5283A)',
    name: 'Mark H.', role: 'CEO, CloudVelocity',
    text: 'Built our entire DevOps infrastructure from scratch on AWS. Zero-downtime deployments, auto-scaling, and monitoring that actually alerts us before users notice issues. Highly recommend.',
  },
  {
    initials: 'NP', grad: 'linear-gradient(135deg,#EF4444,#F59E0B)',
    name: 'Nina P.', role: 'Founder, Launchpad AI',
    text: 'Our SaaS MVP went from wireframe to live product in 6 weeks. The codebase is clean, test coverage is excellent, and the team handled every pivot without complaint. Will hire again.',
  },
  {
    initials: 'AT', grad: 'linear-gradient(135deg,#0EA5E9,#10B981)',
    name: 'Alex T.', role: 'CTO, TrustLayer',
    text: 'Implemented our SSI credential system in record time. Deep knowledge of W3C DID specs, Hyperledger Indy, and the broader identity ecosystem. This is niche expertise that\'s genuinely hard to find.',
  },
];

const STACK = [
  { icon: '⚛️', name: 'React / Next.js' },
  { icon: '🟢', name: 'Node.js' },
  { icon: '🐍', name: 'Python' },
  { icon: '🦀', name: 'TypeScript' },
  { icon: '🗃️', name: 'PostgreSQL' },
  { icon: '🔴', name: 'Redis' },
  { icon: '🐳', name: 'Docker' },
  { icon: '☸️', name: 'Kubernetes' },
  { icon: '🌩️', name: 'AWS' },
  { icon: '🤖', name: 'LangChain' },
  { icon: '⚡', name: 'Solidity' },
  { icon: '🔗', name: 'Hyperledger' },
];

const MARQUEE_ITEMS = [
  'AI Agents', 'Web3 & Blockchain', 'SaaS Development', 'DevOps & Cloud',
  'Real Estate Tech', 'Self-Sovereign Identity', 'LLM Integration',
  'Smart Contracts', 'React & Next.js', 'AWS & GCP',
];

// ── COMPONENTS ────────────────────────────────────────────────────────────────

function ProjectVisual1() {
  return (
    <svg viewBox="0 0 560 230" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="g1" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#E5283A" stopOpacity="0.3"/>
          <stop offset="100%" stopColor="#E5283A" stopOpacity="0"/>
        </radialGradient>
      </defs>
      <circle cx="280" cy="115" r="120" fill="url(#g1)"/>
      <line x1="280" y1="115" x2="140" y2="60" stroke="#E5283A" strokeOpacity="0.25" strokeWidth="1"/>
      <line x1="280" y1="115" x2="420" y2="55" stroke="#E5283A" strokeOpacity="0.25" strokeWidth="1"/>
      <line x1="280" y1="115" x2="430" y2="175" stroke="#FF4D5E" strokeOpacity="0.2" strokeWidth="1"/>
      <line x1="280" y1="115" x2="150" y2="180" stroke="#FF4D5E" strokeOpacity="0.2" strokeWidth="1"/>
      <line x1="280" y1="115" x2="280" y2="30" stroke="#7A1420" strokeOpacity="0.2" strokeWidth="1"/>
      <circle cx="280" cy="115" r="14" fill="#E5283A" fillOpacity="0.9"/>
      <circle cx="140" cy="60" r="8" fill="#E5283A" fillOpacity="0.5"/>
      <circle cx="420" cy="55" r="6" fill="#FF4D5E" fillOpacity="0.5"/>
      <circle cx="430" cy="175" r="9" fill="#7A1420" fillOpacity="0.5"/>
      <circle cx="150" cy="180" r="7" fill="#E5283A" fillOpacity="0.4"/>
      <circle cx="280" cy="30" r="5" fill="#FF4D5E" fillOpacity="0.6"/>
      <text x="105" y="62" fill="#E5283A" fillOpacity="0.6" fontFamily="JetBrains Mono" fontSize="9">triage</text>
      <text x="432" y="58" fill="#FF4D5E" fillOpacity="0.6" fontFamily="JetBrains Mono" fontSize="9">classify</text>
      <text x="438" y="177" fill="#7A1420" fillOpacity="0.6" fontFamily="JetBrains Mono" fontSize="9">respond</text>
      <text x="100" y="183" fill="#E5283A" fillOpacity="0.5" fontFamily="JetBrains Mono" fontSize="9">handoff</text>
      <text x="258" y="26" fill="#FF4D5E" fillOpacity="0.6" fontFamily="JetBrains Mono" fontSize="9">route</text>
      <text x="263" y="120" fill="white" fillOpacity="0.9" fontFamily="JetBrains Mono" fontSize="8" textAnchor="middle">AI</text>
    </svg>
  );
}

function ProjectVisual2() {
  return (
    <svg viewBox="0 0 560 230" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="g2" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#10B981" stopOpacity="0.25"/>
          <stop offset="100%" stopColor="#10B981" stopOpacity="0"/>
        </radialGradient>
      </defs>
      <circle cx="280" cy="115" r="140" fill="url(#g2)"/>
      <polygon points="280,70 315,90 315,130 280,150 245,130 245,90" fill="none" stroke="#10B981" strokeOpacity="0.35" strokeWidth="1.5"/>
      <polygon points="280,55 330,83 330,138 280,166 230,138 230,83" fill="none" stroke="#10B981" strokeOpacity="0.15" strokeWidth="1"/>
      <polygon points="280,38 348,76 348,150 280,188 212,150 212,76" fill="none" stroke="#10B981" strokeOpacity="0.08" strokeWidth="1"/>
      <circle cx="280" cy="110" r="12" fill="#10B981" fillOpacity="0.7"/>
      <circle cx="180" cy="80" r="5" fill="#10B981" fillOpacity="0.4"/>
      <circle cx="380" cy="80" r="5" fill="#FF4D5E" fillOpacity="0.4"/>
      <line x1="180" y1="80" x2="245" y2="90" stroke="#10B981" strokeOpacity="0.2" strokeWidth="1"/>
      <line x1="380" y1="80" x2="315" y2="90" stroke="#FF4D5E" strokeOpacity="0.2" strokeWidth="1"/>
      <text x="270" y="114" fill="white" fillOpacity="0.9" fontFamily="JetBrains Mono" fontSize="8" textAnchor="middle">ETH</text>
    </svg>
  );
}

function ProjectVisual3() {
  return (
    <svg viewBox="0 0 560 230" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="g3" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.2"/>
          <stop offset="100%" stopColor="#F59E0B" stopOpacity="0"/>
        </radialGradient>
      </defs>
      <circle cx="280" cy="115" r="130" fill="url(#g3)"/>
      <circle cx="220" cy="100" r="18" fill="none" stroke="#F59E0B" strokeOpacity="0.2" strokeWidth="1"/>
      <circle cx="220" cy="100" r="6" fill="#F59E0B" fillOpacity="0.6"/>
      <circle cx="310" cy="130" r="22" fill="none" stroke="#EF4444" strokeOpacity="0.15" strokeWidth="1"/>
      <circle cx="310" cy="130" r="7" fill="#EF4444" fillOpacity="0.5"/>
      <circle cx="370" cy="85" r="14" fill="none" stroke="#F59E0B" strokeOpacity="0.2" strokeWidth="1"/>
      <circle cx="370" cy="85" r="5" fill="#F59E0B" fillOpacity="0.4"/>
      <line x1="100" y1="115" x2="460" y2="115" stroke="rgba(245,158,11,0.08)" strokeWidth="1"/>
      <line x1="280" y1="30" x2="280" y2="200" stroke="rgba(245,158,11,0.08)" strokeWidth="1"/>
      <text x="258" y="193" fill="#F59E0B" fillOpacity="0.4" fontFamily="JetBrains Mono" fontSize="8">2M+ listings</text>
    </svg>
  );
}

function ProjectVisual4() {
  return (
    <svg viewBox="0 0 560 230" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="g4" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#7A1420" stopOpacity="0.25"/>
          <stop offset="100%" stopColor="#7A1420" stopOpacity="0"/>
        </radialGradient>
      </defs>
      <circle cx="280" cy="115" r="130" fill="url(#g4)"/>
      <circle cx="280" cy="115" r="60" fill="none" stroke="#7A1420" strokeOpacity="0.3" strokeWidth="1.5" strokeDasharray="4 4"/>
      <circle cx="280" cy="115" r="85" fill="none" stroke="#7A1420" strokeOpacity="0.15" strokeWidth="1" strokeDasharray="2 6"/>
      <circle cx="280" cy="115" r="110" fill="none" stroke="#7A1420" strokeOpacity="0.07" strokeWidth="1"/>
      <path d="M280,88 L300,96 L300,116 C300,126 280,134 280,134 C280,134 260,126 260,116 L260,96 Z" fill="#7A1420" fillOpacity="0.25" stroke="#7A1420" strokeOpacity="0.5" strokeWidth="1.5"/>
      <text x="274" y="114" fill="white" fillOpacity="0.9" fontFamily="JetBrains Mono" fontSize="9">DID</text>
      <circle cx="280" cy="55" r="6" fill="#7A1420" fillOpacity="0.6"/>
      <circle cx="332" cy="82" r="5" fill="#E5283A" fillOpacity="0.5"/>
      <circle cx="332" cy="148" r="5" fill="#E5283A" fillOpacity="0.5"/>
      <circle cx="280" cy="175" r="6" fill="#7A1420" fillOpacity="0.5"/>
      <circle cx="228" cy="148" r="5" fill="#E5283A" fillOpacity="0.5"/>
      <circle cx="228" cy="82" r="5" fill="#E5283A" fillOpacity="0.5"/>
    </svg>
  );
}

const PROJECTS = [
  {
    visual: <ProjectVisual1/>,
    bg: 'linear-gradient(135deg,#080420 0%,#130830 40%,#081428 100%)',
    badges: ['AI Agents', 'SaaS'],
    name: 'NeuralDesk',
    desc: 'AI-powered customer support platform with LLM-based triage, auto-response agents, and intelligent handoff — reducing support volume by 68%.',
    tech: ['Next.js', 'LangChain', 'GPT-4', 'Redis'],
  },
  {
    visual: <ProjectVisual2/>,
    bg: 'linear-gradient(135deg,#041A08 0%,#052E12 40%,#041A10 100%)',
    badges: ['Web3', 'DeFi'],
    name: 'ChainVault',
    desc: 'Multi-chain DeFi portfolio tracker with real-time asset valuation, yield farming analytics, and cross-chain bridge aggregation across 12 networks.',
    tech: ['Solidity', 'Ethers.js', 'The Graph', 'React'],
  },
  {
    visual: <ProjectVisual3/>,
    bg: 'linear-gradient(135deg,#1A0808 0%,#2E0F0F 40%,#1A080F 100%)',
    badges: ['PropTech', 'RETS'],
    name: 'PropEdge',
    desc: 'Real estate analytics SaaS with live MLS data ingestion, predictive pricing models, and interactive property discovery maps with 2M+ listings.',
    tech: ['RETS API', 'Python', 'Mapbox', 'FastAPI'],
  },
  {
    visual: <ProjectVisual4/>,
    bg: 'linear-gradient(135deg,#060818 0%,#0A0F28 40%,#060818 100%)',
    badges: ['SSI', 'Identity'],
    name: 'IdentityOS',
    desc: 'Self-sovereign identity platform on W3C DID standards, enabling portable verifiable credentials for healthcare, education, and financial institutions.',
    tech: ['DID', 'Hyperledger', 'Node.js', 'PostgreSQL'],
  },
];

// ── HOOKS ─────────────────────────────────────────────────────────────────────

function useTilt() {
  useEffect(() => {
    const cards = document.querySelectorAll('.svc-card, .proj-card, .rev-card, .step-card');
    const handlers = [];
    cards.forEach(card => {
      const move = e => {
        const r = card.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5;
        const y = (e.clientY - r.top) / r.height - 0.5;
        card.style.transform = `perspective(900px) rotateY(${x * 7}deg) rotateX(${-y * 7}deg) translateY(-4px)`;
        card.style.setProperty('--mx', `${(x + 0.5) * 100}%`);
        card.style.setProperty('--my', `${(y + 0.5) * 100}%`);
      };
      const leave = () => { card.style.transform = ''; };
      card.addEventListener('mousemove', move);
      card.addEventListener('mouseleave', leave);
      handlers.push([card, move, leave]);
    });
    return () => handlers.forEach(([c, m, l]) => {
      c.removeEventListener('mousemove', m);
      c.removeEventListener('mouseleave', l);
    });
  }, []);
}

function useMagnetic() {
  useEffect(() => {
    const btns = document.querySelectorAll('.btn-primary, .btn-secondary');
    const handlers = [];
    btns.forEach(btn => {
      const move = e => {
        const r = btn.getBoundingClientRect();
        const x = e.clientX - r.left - r.width / 2;
        const y = e.clientY - r.top - r.height / 2;
        btn.style.transform = `translate(${x * 0.18}px, ${y * 0.3}px)`;
      };
      const leave = () => { btn.style.transform = ''; };
      btn.addEventListener('mousemove', move);
      btn.addEventListener('mouseleave', leave);
      handlers.push([btn, move, leave]);
    });
    return () => handlers.forEach(([b, m, l]) => {
      b.removeEventListener('mousemove', m);
      b.removeEventListener('mouseleave', l);
    });
  }, []);
}

function useScrollProgress() {
  useEffect(() => {
    const bar = document.getElementById('scroll-progress');
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      if (bar) bar.style.transform = `scaleX(${h > 0 ? window.scrollY / h : 0})`;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
}

function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('visible'); observer.unobserve(e.target); }
      }),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

function useCounters() {
  useEffect(() => {
    const counters = document.querySelectorAll('[data-target]');
    const observer = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        const el = e.target;
        if (el.dataset.counted) return;
        el.dataset.counted = true;
        const target = parseFloat(el.dataset.target);
        const suffix = el.dataset.suffix || '';
        const isDecimal = String(target).includes('.');
        const dur = 1800;
        const start = performance.now();
        const tick = now => {
          const t = Math.min((now - start) / dur, 1);
          const ease = 1 - Math.pow(1 - t, 3);
          const val = target * ease;
          el.textContent = isDecimal
            ? val.toFixed(1) + suffix
            : target >= 1000
              ? Math.floor(val).toLocaleString() + suffix
              : Math.floor(val) + suffix;
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        observer.unobserve(el);
      });
    }, { threshold: 0.5 });
    counters.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

function useNav() {
  useEffect(() => {
    const nav = document.getElementById('main-nav');
    const onScroll = () => nav?.classList.toggle('scrolled', window.scrollY > 30);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
}

function useCursorGlow() {
  useEffect(() => {
    const glow = document.getElementById('cursor-glow');
    const move = e => { if (glow) { glow.style.left = e.clientX + 'px'; glow.style.top = e.clientY + 'px'; } };
    document.addEventListener('mousemove', move);
    return () => document.removeEventListener('mousemove', move);
  }, []);
}

// ── APP ───────────────────────────────────────────────────────────────────────

export default function Home() {
  useScrollReveal();
  useCounters();
  useNav();
  useCursorGlow();
  useTilt();
  useMagnetic();
  useScrollProgress();

  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  const marqueeItems = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS];

  return (
    <ClickSpark sparkColor="#E5283A" sparkSize={11} sparkRadius={24} sparkCount={8} duration={500}>
      <div className="grid-bg" />
      <div className="noise-overlay" />
      <div className="cursor-glow" id="cursor-glow" />
      <div className="scroll-progress" id="scroll-progress" />

      {/* NAV */}
      <nav id="main-nav" className={menuOpen ? 'scrolled' : ''}>
        <a href="#home" className="nav-logo" onClick={closeMenu}>
          <img src="/images/sc-logo.svg" alt="SpreadCode" className="nav-logo-mark" />
        </a>
        <button
          className={`nav-hamburger${menuOpen ? ' open' : ''}`}
          onClick={() => setMenuOpen(o => !o)}
          aria-label="Toggle menu"
        >
          <span /><span /><span />
        </button>
        <ul className={`nav-links${menuOpen ? ' nav-links--open' : ''}`}>
          <li><a href="#services" onClick={closeMenu}>Services</a></li>
          <li><a href="#work" onClick={closeMenu}>Work</a></li>
          <li><a href="#reviews" onClick={closeMenu}>Reviews</a></li>
          <li><a href="#playground" onClick={closeMenu}>Playground</a></li>
          <li><a href="#process" onClick={closeMenu}>Process</a></li>
          <li><a href="#stack" onClick={closeMenu}>Stack</a></li>
          <li><a href="#contact" className="nav-cta" onClick={closeMenu}>Start a Project →</a></li>
        </ul>
      </nav>

      {/* HERO */}
      <section className="hero" id="home">
        <div className="aurora-wrap">
          <LiquidEther
            colors={['#E5283A', '#FF4D5E', '#7A1420']}
            mouseForce={18}
            cursorSize={110}
            resolution={0.5}
            autoDemo
            autoSpeed={0.4}
            autoIntensity={1.8}
            takeoverDuration={0.3}
            autoResumeDelay={2400}
            autoRampDuration={0.6}
          />
        </div>

        <div className="hero-eyebrow">AI AGENTS OR ENTERPRISE SAAS</div>

        <h1 className="hero-title hero-title--condensed">
          <span className="t1">We Are Inevitable.</span>
          <span className="t1">So Is Your Launch.</span>
        </h1>

        <div className="hero-quote">
          <p className="hero-quote-text">
            20,000+ hours engineered. From AI agents to production-grade SaaS — built to launch, not just to demo.
          </p>
          <div className="hero-quote-divider" />
          <div className="hero-quote-author">Islamabad · Worldwide</div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="marquee-wrap">
        <div className="marquee-track">
          {marqueeItems.map((item, i) => (
            <span className="marquee-item" key={i}>
              <span className="m-dot" />{item}
            </span>
          ))}
        </div>
      </div>

      {/* STATS */}
      <section className="stats-section" id="stats">
        <div className="stats-inner reveal">
          {[
            { target: '20000', suffix: '+', label: 'Hours Engineered' },
            { target: '12', suffix: '+', label: 'Technologies Mastered' },
            { target: '2', suffix: 'h', label: 'Avg. Response Time' },
          ].map((s, i) => (
            <div className="stat-card" key={i}>
              <span className="stat-num" data-target={s.target} data-suffix={s.suffix}>0</span>
              <span className="stat-lbl">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="services-section" id="services">
        <div className="sec-header reveal">
          <div>
            <div className="sec-label"><DecryptedText text="What We Do" animateOn="view" sequential speed={35} /></div>
            <h2 className="sec-title">Services Built<br/>for Scale</h2>
          </div>
          <p className="sec-right">From idea to infrastructure — we cover every layer of the modern software stack.</p>
        </div>
        <div className="services-grid">
          {SERVICES.map((s, i) => (
            <div className={`svc-card reveal${i % 3 === 1 ? ' reveal-d1' : i % 3 === 2 ? ' reveal-d2' : ''}`} key={i}>
              <div className="svc-icon">{s.icon}</div>
              <div className="svc-name">{s.name}</div>
              <p className="svc-desc">{s.desc}</p>
              <div className="svc-tags">{s.tags.map(t => <span className="tag" key={t}>{t}</span>)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PROJECTS */}
      <section className="projects-section" id="work">
        <div className="sec-header reveal">
          <div>
            <div className="sec-label"><DecryptedText text="Selected Work" animateOn="view" sequential speed={35} /></div>
            <h2 className="sec-title">Projects That<br/>Shipped</h2>
          </div>
          <p className="sec-right">Real-world systems we've built, launched, and scaled for clients worldwide.</p>
        </div>
        <div className="projects-grid">
          {PROJECTS.map((p, i) => (
            <div className={`proj-card reveal${i % 2 === 1 ? ' reveal-d1' : ''}`} key={i}>
              <div className="proj-visual" style={{ background: p.bg }}>
                {p.visual}
                <GlareHover width="100%" height="100%" background="transparent" borderColor="transparent" borderRadius="0" glareColor="#ffffff" glareOpacity={0.18} glareSize={280} transitionDuration={800} style={{ position: 'absolute', inset: 0 }} />
                <div className="proj-badges">
                  {p.badges.map(b => <span className="proj-badge" key={b}>{b}</span>)}
                </div>
              </div>
              <div className="proj-body">
                <div className="proj-name">{p.name}</div>
                <p className="proj-desc">{p.desc}</p>
                <div className="proj-meta">
                  <div className="proj-techs">{p.tech.map(t => <span className="tech" key={t}>{t}</span>)}</div>
                  <div className="proj-arrow">→</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* REVIEWS */}
      <section className="reviews-section" id="reviews">
        <div className="sec-header reveal">
          <div>
            <div className="sec-label"><DecryptedText text="Client Reviews" animateOn="view" sequential speed={35} /></div>
            <h2 className="sec-title">What Clients<br/>Are Saying</h2>
          </div>
          <p className="sec-right">We let the work speak first — then our clients do.</p>
        </div>
        <div className="reviews-grid">
          {REVIEWS.map((r, i) => (
            <div className={`rev-card reveal${i % 3 === 1 ? ' reveal-d1' : i % 3 === 2 ? ' reveal-d2' : ''}`} key={i}>
              <div className="rev-stars">★★★★★</div>
              <p className="rev-quote">"{r.text}"</p>
              <div className="rev-author">
                <div className="author-av" style={{ background: r.grad }}>{r.initials}</div>
                <div>
                  <div className="author-name">{r.name}</div>
                  <div className="author-role">{r.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PLAYGROUND / TERMINAL */}
      <section className="terminal-section" id="playground">
        <div className="sec-header reveal">
          <div>
            <div className="sec-label"><DecryptedText text="Interactive Playground" animateOn="view" sequential speed={35} /></div>
            <h2 className="sec-title">Talk to Us Like<br/>an Engineer</h2>
          </div>
          <p className="sec-right">A real terminal. Type <code>help</code> to explore, <code>snake</code> to play, or <code>sudo hire-us</code> to start a project.</p>
        </div>
        <div className="terminal-wrap">
          <Terminal />
        </div>
      </section>

      {/* PROCESS */}
      <section className="process-section" id="process">
        <div className="sec-header reveal" style={{ marginBottom: 52 }}>
          <div>
            <div className="sec-label"><DecryptedText text="How We Work" animateOn="view" sequential speed={35} /></div>
            <h2 className="sec-title">From Brief<br/>to Launch</h2>
          </div>
          <p className="sec-right">A disciplined process that keeps every project on track, on budget, and exactly on vision.</p>
        </div>
        <div className="process-inner">
          {[
            { n: '01', name: 'Discovery', desc: 'Deep dive into your product, users, and constraints. We map the full landscape before writing a single line of code.' },
            { n: '02', name: 'Architecture', desc: 'System design, tech stack selection, and API contracts. We build the blueprint that scales to 10x your current load.' },
            { n: '03', name: 'Engineering', desc: 'Iterative development with weekly demos. You see progress constantly — no black boxes, no surprises at the end.' },
            { n: '04', name: 'Launch & Scale', desc: 'Deployment, monitoring, and ongoing support. We don\'t vanish after go-live — we make sure your product thrives.' },
          ].map((s, i) => (
            <div className={`step-card reveal reveal-d${i}`} key={i}>
              <span className="step-num">{s.n}</span>
              <div className="step-name">{s.name}</div>
              <p className="step-desc">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* STACK */}
      <section className="stack-section" id="stack">
        <div className="sec-header reveal">
          <div>
            <div className="sec-label"><DecryptedText text="Technologies" animateOn="view" sequential speed={35} /></div>
            <h2 className="sec-title">The Stack We<br/>Ship With</h2>
          </div>
          <p className="sec-right">Proven tools, modern patterns — chosen for each project's needs, not trends.</p>
        </div>
        <div className="stack-grid reveal">
          {STACK.map((s, i) => (
            <div className="stack-item" key={i}>
              <span className="stack-icon">{s.icon}</span>
              <span className="stack-name">{s.name}</span>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section" id="contact">
        <div className="cta-orb" />
        <div className="cta-inner reveal">
          <div className="sec-label" style={{ textAlign: 'center', marginBottom: 20 }}><DecryptedText text="Ready to Build?" animateOn="view" sequential speed={35} /></div>
          <h2 className="cta-title">
            Let's Build Something<br/>
            <span className="grad">Remarkable Together</span>
          </h2>
          <p className="cta-sub">
            Share your idea. We'll tell you exactly how to build it — architecture, timeline, and cost upfront. Then we build it together.
          </p>
          <div className="hero-actions" style={{ marginBottom: 0 }}>
            <StarBorder as="a" href="mailto:connect@spreadcode.org" className="cta-star" color="#FF4D5E" speed="5s" thickness={1}>Get in Touch →</StarBorder>
            <StarBorder as="a" href="#work" className="cta-star cta-star--ghost" color="#7A1420" speed="7s" thickness={1}>See Case Studies</StarBorder>
          </div>
          <p className="cta-note">Avg. response time: under 2 hours · Based in Islamabad, PKT · Works globally</p>
        </div>
      </section>

      <ChatWidget />

      {/* FOOTER */}
      <footer>
        <div className="footer-dotgrid">
          <DotGrid
            dotSize={3}
            gap={22}
            baseColor="#1C1C36"
            activeColor="#E5283A"
            proximity={110}
            shockRadius={200}
            shockStrength={3}
            returnDuration={1.2}
          />
        </div>
        <div className="footer-left">
          <div className="footer-logo">
            <img src="/images/sc-logo.svg" alt="SpreadCode" className="footer-logo-mark" />
          </div>
          <div className="footer-copy">© {new Date().getFullYear()} SpreadCode. All rights reserved.</div>
        </div>
        <div className="footer-links">
          <a href="#services">Services</a>
          <a href="#work">Work</a>
          <a href="#reviews">Reviews</a>
          <a href="#process">Process</a>
          <a href="mailto:connect@spreadcode.org">Contact</a>
        </div>
      </footer>
    </ClickSpark>
  );
}
